import { Component, OnInit, OnDestroy } from '@angular/core';
import { Router, ActivatedRoute } from '@angular/router';
import { FormBuilder, FormGroup, Validators, FormArray, FormControl } from '@angular/forms';
import { Subject } from 'rxjs/Subject';
import { takeUntil, finalize } from 'rxjs/operators';
import { NgxPermissionsService } from 'ngx-permissions';

import { VehiclesFilesService } from '../../../_services/vehicles-files.service';
import { ToasterErrorService } from '../../../_services/toaster.service';
import {
    IInvestigationFormType,
    INVESTIGATION_PERMISSION,
    INVESTIGATION_STATUS
} from '../../../_models/vehicle-file';

@Component({
    moduleId: module.id,
    selector: 'investigation-app',
    templateUrl: './investigation.component.html',
    styleUrls: ['./investigation.component.scss']
})
export class InvestigationComponent implements OnInit, OnDestroy {
    private destroy$ = new Subject<void>();
    
    toasterconfig: any = this.toasterErrorService.toasterconfig;
    investigationForm: FormGroup;
    investigationData: any = null;
    isLoading: boolean = false;
    isSaving: boolean = false;
    investigationId: number;
    activityId: number;

    // Exposed for the template
    STATUS = INVESTIGATION_STATUS;
    PERMISSION = INVESTIGATION_PERMISSION;

    // Form type selection, only in play while the investigation has no form
    formTypes: IInvestigationFormType[] = [];
    selectedFormTypeId: number = null;
    isLoadingFormTypes: boolean = false;
    isSavingFormType: boolean = false;

    // Sign off and approval
    isSigningOff: boolean = false;
    isReviewing: boolean = false;
    isDownloadingPdf: boolean = false;
    reviewComment: string = '';
    approvalSignature: string = '';

    /***
     * The questions in play, keyed by id, built from the answers when the form
     * is populated. They are per-investigation copies rather than the form
     * type's configured questions.
     */
    private questionsById: { [id: number]: any } = {};


    // Form arrays for dynamic questions
    get answersArray(): FormArray {
        return this.investigationForm.get('answers') as FormArray;
    }

    constructor(
        private router: Router,
        private route: ActivatedRoute,
        private fb: FormBuilder,
        private vehicleFileService: VehiclesFilesService,
        private toasterErrorService: ToasterErrorService,
        private permissionsService: NgxPermissionsService
    ) {
        this.createForm();
    }

    ngOnInit() {
        // Get activityId and investigationId from route params
        this.route.params.pipe(takeUntil(this.destroy$)).subscribe(params => {
            this.activityId = +params['activityId'];
            this.investigationId = +params['investigationId'];
            if (this.activityId && this.investigationId) {
                this.loadInvestigationDetails();
            }
        });
    }

    ngOnDestroy() {
        this.destroy$.next();
        this.destroy$.complete();
    }

    createForm() {
        this.investigationForm = this.fb.group({
            answers: this.fb.array([]),
            signoff_signature: ['']
        });
    }

    loadInvestigationDetails() {
        this.isLoading = true;
        
        if (this.activityId && this.investigationId) {
            this.vehicleFileService.getInvestigationDetails(this.activityId, this.investigationId)
                .pipe(
                    takeUntil(this.destroy$),
                    finalize(() => this.isLoading = false)
                )
                .subscribe(
                    (data) => {
                        this.applyInvestigation(data);
                    },
                    (error) => {
                        console.error('Error loading investigation details:', error);
                        this.toasterErrorService.toasterErr('Failed to load investigation details');
                    }
                );
        } else {
            this.isLoading = false;
            this.toasterErrorService.toasterErr('Missing activity ID or investigation ID');
        }
    }

    /***
     * Builds the form from the investigation's answers.
     *
     * The answers are the source of the questions, not the template. Each one
     * points at a copy of the question taken when the form type was chosen, so
     * the form shows what was asked at the time rather than what the form type
     * asks now. The copies carry their own ids, so pairing answers to template
     * questions by id would never match.
     */
    populateForm(data: any) {
        // Set signoff signature form control
        this.investigationForm.get('signoff_signature').setValue(data.signoff_signature || '');

        // Clear existing answers array
        while (this.answersArray.length !== 0) {
            this.answersArray.removeAt(0);
        }

        this.questionsById = {};

        const answers = data.answers || [];
        const formEditable = this.isEditableStatus(data.status) && this.canFillForm();

        answers.forEach(answer => {
            const question = answer.question;
            if (!question) {
                return;
            }

            this.questionsById[question.id] = question;

            // Format date answers for HTML date input
            let formattedAnswer = answer.answer;
            if (question.answer_type === 'DATE' && answer.answer) {
                formattedAnswer = this.formatDateForInput(answer.answer);
            }

            // An answer the system filled in stays locked whatever the status
            const disabled = !formEditable || !!answer.read_only;

            this.addAnswer({
                id: answer.id,
                question: question.id,
                answer: formattedAnswer,
                attachment: answer.attachment,
                read_only: !!answer.read_only
            }, disabled);
        });
    }

    /***
     * Takes an investigation payload and renders it. Every action responds
     * with the full detail, so this is the single place state is applied.
     */
    applyInvestigation(data: any) {
        this.investigationData = data;
        this.populateForm(data);

        if (this.needsFormType()) {
            this.loadFormTypes();
        }
    }

    /***
     * Whether the form's answers can be edited in this status. A form is open
     * while it is being filled in, and again after an approver sends it back.
     */
    isEditableStatus(status: string): boolean {
        return status === INVESTIGATION_STATUS.PENDING ||
            status === INVESTIGATION_STATUS.APPROVAL_REJECTED;
    }

    /** True while no form type has been chosen, so there is no form yet */
    needsFormType(): boolean {
        return !!this.investigationData &&
            this.investigationData.status === INVESTIGATION_STATUS.REQUIRES_FORM_TYPE;
    }

    /** True once the form has been signed and is waiting on an approver */
    isAwaitingApproval(): boolean {
        return !!this.investigationData &&
            this.investigationData.status === INVESTIGATION_STATUS.REQUIRES_APPROVAL;
    }

    /** True when an approver sent the form back to be amended */
    isRejected(): boolean {
        return !!this.investigationData &&
            this.investigationData.status === INVESTIGATION_STATUS.APPROVAL_REJECTED;
    }

    isComplete(): boolean {
        return !!this.investigationData &&
            this.investigationData.status === INVESTIGATION_STATUS.COMPLETE;
    }

    /** Whether the user holds the module that lets them fill in the form */
    canFillForm(): boolean {
        return !!this.permissionsService.getPermission(this.PERMISSION.FILL_FORM);
    }

    /** Whether the answers on screen can be edited */
    canEditAnswers(): boolean {
        return !!this.investigationData &&
            this.isEditableStatus(this.investigationData.status) &&
            this.canFillForm();
    }

    loadFormTypes() {
        this.isLoadingFormTypes = true;
        this.vehicleFileService.getInvestigationFormTypes(this.activityId, this.investigationId)
            .pipe(
                takeUntil(this.destroy$),
                finalize(() => this.isLoadingFormTypes = false)
            )
            .subscribe(
                (formTypes) => {
                    this.formTypes = formTypes || [];
                },
                (error) => {
                    console.error('Error loading investigation form types:', error);
                    this.toasterErrorService.toasterErr('Failed to load the available forms');
                }
            );
    }

    /***
     * Chooses the form type, which builds the form. This cannot be undone, so
     * it is confirmed first.
     */
    selectFormType() {
        if (!this.selectedFormTypeId || this.isSavingFormType) {
            return;
        }

        const _formType = this.formTypes.filter(f => f.id === this.selectedFormTypeId)[0];
        const _name = _formType ? _formType.title : '';
        if (!window.confirm(
            `Create this investigation using the "${_name}" form? This cannot be changed afterwards.`)) {
            return;
        }

        this.isSavingFormType = true;
        this.vehicleFileService.selectInvestigationFormType(
            this.activityId, this.investigationId, this.selectedFormTypeId)
            .pipe(
                takeUntil(this.destroy$),
                finalize(() => this.isSavingFormType = false)
            )
            .subscribe(
                (data) => {
                    this.formTypes = [];
                    this.selectedFormTypeId = null;
                    this.applyInvestigation(data);
                    this.toasterErrorService.toasterSuccess('Form created, it can now be filled in');
                },
                (error) => {
                    this.toasterErrorService.toasterErr(
                        this.firstApiError(error, 'form_type', 'Failed to create the form'));
                }
            );
    }

    /** Whether signing off sends the form for approval or completes it */
    requiresApproval(): boolean {
        return !!this.investigationData &&
            !!this.investigationData.form_type &&
            this.investigationData.form_type.requires_approval;
    }

    /** The most recent decision an approver made, or null */
    latestReview() {
        const _reviews = this.investigationData ? this.investigationData.reviews : null;
        return _reviews && _reviews.length ? _reviews[0] : null;
    }

    /** The rejection an approver sent the form back with, while it is back */
    rejectionComment(): string {
        if (!this.isRejected()) {
            return '';
        }
        const _review = this.latestReview();
        return _review && _review.decision === 'REJECTED' ? _review.comment : '';
    }

    /***
     * Signs off the filled in form. Closes editing, so it is confirmed first.
     */
    signOff() {
        if (this.isSigningOff || !this.canEditAnswers()) {
            return;
        }

        if (this.investigationForm.invalid) {
            this.toasterErrorService.toasterErr('Answer every question before signing off');
            return;
        }

        if (!this.hasSignature()) {
            this.toasterErrorService.toasterErr('Sign the form before submitting it');
            return;
        }

        const _message = this.requiresApproval()
            ? 'Submit this form for approval? It cannot be edited while an approver has it.'
            : 'Submit this form? It will be marked complete and cannot be edited afterwards.';
        if (!window.confirm(_message)) {
            return;
        }

        // The answers are saved first. Sign off only stores the signature and
        // moves the status, so signing without saving would discard whatever
        // the user typed and then lock the form.
        this.isSigningOff = true;
        const _signature = this.investigationForm.get('signoff_signature').value;
        const _requiresApproval = this.requiresApproval();

        this.vehicleFileService.updateInvestigation(
            this.activityId, this.investigationId, this.buildUpdatePayload())
            .pipe(takeUntil(this.destroy$))
            .subscribe(
                () => {
                    this.vehicleFileService.signOffInvestigation(
                        this.activityId, this.investigationId, _signature)
                        .pipe(
                            takeUntil(this.destroy$),
                            finalize(() => this.isSigningOff = false)
                        )
                        .subscribe(
                            (data) => {
                                this.applyInvestigation(data);
                                this.toasterErrorService.toasterSuccess(
                                    _requiresApproval ? 'Sent for approval' : 'Investigation complete');
                            },
                            (error) => {
                                // The answers are saved, so the form stays editable
                                this.toasterErrorService.toasterErr(
                                    this.firstApiError(error, 'status',
                                        'Answers saved, but signing off failed. Try again.'));
                            }
                        );
                },
                (error) => {
                    this.isSigningOff = false;
                    this.toasterErrorService.toasterErr(
                        this.firstApiError(error, 'status',
                            'Could not save your answers, so nothing was signed off'));
                }
            );
    }

    /***
     * Records an approver's decision. A comment is required on a rejection,
     * which the backend also enforces.
     */
    review(decision: 'APPROVED' | 'REJECTED') {
        if (this.isReviewing || !this.isAwaitingApproval()) {
            return;
        }

        const _comment = (this.reviewComment || '').trim();
        if (decision === 'REJECTED' && !_comment) {
            this.toasterErrorService.toasterErr('Add a comment explaining why it is being rejected');
            return;
        }

        if (decision === 'APPROVED' && !this.approvalSignature) {
            this.toasterErrorService.toasterErr('Sign the form before approving it');
            return;
        }

        const _message = decision === 'APPROVED'
            ? 'Approve this investigation?'
            : 'Reject this investigation and send it back to be amended?';
        if (!window.confirm(_message)) {
            return;
        }

        this.isReviewing = true;
        this.vehicleFileService.reviewInvestigation(
            this.activityId, this.investigationId, decision, _comment, this.approvalSignature)
            .pipe(
                takeUntil(this.destroy$),
                finalize(() => this.isReviewing = false)
            )
            .subscribe(
                (data) => {
                    this.reviewComment = '';
                    this.approvalSignature = '';
                    this.applyInvestigation(data);
                    this.toasterErrorService.toasterSuccess(
                        decision === 'APPROVED' ? 'Investigation approved' : 'Investigation sent back');
                },
                (error) => {
                    this.toasterErrorService.toasterErr(
                        this.firstApiError(error, 'decision', 'Failed to record the decision'));
                }
            );
    }

    onApprovalSignatureChange(signatureData: string) {
        this.approvalSignature = signatureData;
    }

    /** The PDF only exists once there is a form to render */
    canDownloadPdf(): boolean {
        return !!this.investigationData && !this.needsFormType();
    }

    /***
     * The PDF request needs its auth header, so it is fetched as a blob
     * rather than linked to directly.
     */
    downloadPdf() {
        if (this.isDownloadingPdf || !this.canDownloadPdf()) {
            return;
        }

        this.isDownloadingPdf = true;
        this.vehicleFileService.getInvestigationPdf(this.activityId, this.investigationId)
            .pipe(
                takeUntil(this.destroy$),
                finalize(() => this.isDownloadingPdf = false)
            )
            .subscribe(
                (response) => {
                    const _reg = this.investigationData.vehicle ?
                        this.investigationData.vehicle.registration : '';
                    this.downloadFileResponse(
                        response, `investigation-${this.investigationId}${_reg ? '_' + _reg : ''}`);
                },
                (error) => {
                    this.toasterErrorService.toasterErr('Something went wrong with the PDF download');
                }
            );
    }

    private downloadFileResponse(response, filename?: string) {
        const _filename = filename ? `${filename}.pdf` : 'datalive-download.pdf';
        const _body = response && response._body ? response._body : response;
        const blob = _body instanceof Blob ? _body : new Blob([_body], { type: 'application/pdf' });
        const url = window.URL.createObjectURL(blob);

        if (navigator.msSaveOrOpenBlob) {
            navigator.msSaveBlob(blob, _filename);
        } else {
            const a = document.createElement('a');
            a.href = url;
            a.download = _filename;
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
        }
        window.URL.revokeObjectURL(url);
    }

    /***
     * Pulls the first message off a DRF field-keyed error response, which is
     * how the investigation endpoints report a refused transition.
     */
    private firstApiError(error: any, field: string, fallback: string): string {
        try {
            const _body = error && error.json ? error.json() : (error ? error._body : null);
            const _parsed = typeof _body === 'string' ? JSON.parse(_body) : _body;
            if (_parsed && _parsed[field] && _parsed[field].length) {
                return _parsed[field][0];
            }
        } catch (e) {
            // Fall through to the generic message
        }
        return fallback;
    }

    addAnswer(answerData: any = {}, disabledInput: boolean) {
        // Get question type from template questions
        const questionType = this.getQuestionTypeById(answerData.question);
        
        // Handle DROPDOWN_AND_TEXT type specially
        if (questionType === 'DROPDOWN_AND_TEXT') {
            const dropdownAnswer = answerData.answer ? this.extractDropdownFromAnswer(answerData.answer) : '';
            const textAnswer = answerData.answer ? this.extractTextFromAnswer(answerData.answer) : '';
            
            const answerGroup = this.fb.group({
                id: [answerData.id || null],
                question_id: [answerData.question || answerData.question_id || null, Validators.required],
                dropdown_answer: [{value: dropdownAnswer, disabled:  disabledInput }, Validators.required],
                text_answer: [{value: textAnswer || '', disabled: disabledInput }, Validators.required],
                attachment: [answerData.attachment || null],
                read_only: [!!answerData.read_only],
                remove: [false]
            });
            
            this.answersArray.push(answerGroup);
        } else {
            const answerGroup = this.fb.group({
                id: [answerData.id || null],
                question_id: [answerData.question || answerData.question_id || null, Validators.required],
                answer: [{value: answerData.answer || '', disabled: disabledInput }, this.getAnswerValidators(questionType)],
                attachment: [answerData.attachment || null],
                read_only: [!!answerData.read_only],
                remove: [false]
            });
            
            this.answersArray.push(answerGroup);
        }
    }

    getAnswerValidators(questionType: string) {
        const validators = [];
        
        // Add required validator for most question types
        if (questionType !== 'FILE') {
            validators.push(Validators.required);
        }
        
        // Add specific validators based on question type
        switch (questionType) {
            case 'TEXT':
                validators.push(Validators.maxLength(255));
                break;
            case 'DATE':
                // Date validation can be added here
                break;
            case 'CHOICE':
                // Choice validation can be added here
                break;
        }
        
        return validators;
    }

    onFileSelected(event: any, answerIndex: number) {
        const file = event.target.files[0];
        if (file) {
            // Convert file to base64
            const reader = new FileReader();
            reader.onload = () => {
                const base64 = reader.result as string;
                this.answersArray.at(answerIndex).patchValue({
                    attachment: base64
                });
            };
            reader.readAsDataURL(file);
        }
    }

    /** Sign off needs a signature, though saving a draft does not */
    hasSignature(): boolean {
        return !!this.investigationForm.get('signoff_signature').value;
    }

    onSignatureChange(signatureData: string) {
        this.investigationForm.patchValue({
            signoff_signature: signatureData
        });
    }

    /***
     * Builds the update payload from the form. Shared with sign off, which
     * saves first so that signing cannot capture stale answers.
     */
    buildUpdatePayload() {
            const formData = this.investigationForm.value;

            // An answer the system filled in is disabled, so Angular leaves
            // its value out of the form value entirely. Sending it back would
            // blank it, and it is not the user's to change, so skip it.
            const editableAnswers = formData.answers.filter(answer => !answer.read_only);

            // Prepare answers data
            const answers = editableAnswers.map(answer => {
                // Get question type to handle different answer types
                const questionType = this.getQuestionTypeById(answer.question_id);
                let formattedAnswer = answer.answer;
                
                // Handle DROPDOWN_AND_TEXT type
                if (questionType === 'DROPDOWN_AND_TEXT') {
                    formattedAnswer = this.combineDropdownAndText(answer.dropdown_answer, answer.text_answer);
                }
                // Convert HTML date format back to ISO format for API
                else if (questionType === 'DATE' && answer.answer) {
                    formattedAnswer = this.formatDateForAPI(answer.answer);
                }
                
                const answerData: any = {
                    answer: formattedAnswer
                };
                
                if (answer.id) {
                    answerData.id = answer.id;
                } else {
                    answerData.question_id = answer.question_id;
                }
                
                if (answer.attachment) {
                    answerData.attachment = answer.attachment;
                }
                
                if (answer.remove) {
                    answerData.remove = true;
                }
                
                return answerData;
            });
            // Saving only stores progress. Completing an investigation goes
            // through sign off, so status is not sent here.
            return {
                answers: answers,
                signoff_signature: formData.signoff_signature
            };
    }

    saveInvestigation() {
        if (!this.canEditAnswers()) {
            this.toasterErrorService.toasterErr('This investigation can no longer be edited');
            return;
        }

        if (this.investigationForm.valid) {
            this.isSaving = true;
            const updateData = this.buildUpdatePayload();

            this.vehicleFileService.updateInvestigation(this.activityId, this.investigationId, updateData)
                .pipe(
                    takeUntil(this.destroy$),
                    finalize(() => this.isSaving = false)
                )
                .subscribe(
                    (response) => {
                        this.toasterErrorService.toasterSuccess('Investigation updated successfully');
                        this.loadInvestigationDetails(); // Reload to get updated data
                    },
                    (error) => {
                        console.error('Error updating investigation:', error);
                        this.toasterErrorService.toasterErr('Failed to update investigation');
                    }
                );
        } else {
            this.markFormGroupTouched();
            this.toasterErrorService.toasterErr('Please fill in all required fields');
        }
    }

    markFormGroupTouched() {
        Object.keys(this.investigationForm.controls).forEach(key => {
            const control = this.investigationForm.get(key);
            control.markAsTouched();
            
            if (control instanceof FormArray) {
                control.controls.forEach(arrayControl => {
                    if (arrayControl instanceof FormGroup) {
                        Object.keys(arrayControl.controls).forEach(arrayKey => {
                            arrayControl.get(arrayKey).markAsTouched();
                        });
                    }
                });
            }
        });
    }

    goBack() {
        this.router.navigate(['/vehicle-file/activity']);
    }

    /***
     * Questions come from the answers, keyed when the form was populated.
     * They are per-investigation copies, so they are not on the template.
     */
    getQuestionById(questionId: number) {
        return this.questionsById[questionId] || null;
    }

    getQuestionTypeById(questionId: number): string {
        const question = this.getQuestionById(questionId);
        return question ? question.answer_type : 'TEXT';
    }

    formatDateForInput(dateString: string): string {
        if (!dateString) return '';
        
        try {
            // Convert ISO date string to YYYY-MM-DD format for HTML date input
            const date = new Date(dateString);
            return date.toISOString().split('T')[0];
        } catch (error) {
            console.error('Error formatting date:', error);
            return '';
        }
    }

    formatDateForAPI(dateString: string): string {
        if (!dateString) return '';
        
        try {
            // Convert YYYY-MM-DD format to ISO date string for API
            const date = new Date(dateString + 'T00:00:00.000Z');
            return date.toISOString();
        } catch (error) {
            console.error('Error formatting date for API:', error);
            return '';
        }
    }

    getAnswerType(questionId: number): string {
        const question = this.getQuestionById(questionId);
        return question ? question.answer_type : 'TEXT';
    }

    getAnswerChoices(questionId: number): any[] {
        const question = this.getQuestionById(questionId);
        return question ? question.answer_choices || [] : [];
    }

    isFieldInvalid(fieldName: string, answerIndex?: number): boolean {
        if (answerIndex !== undefined) {
            const answerControl = this.answersArray.at(answerIndex);
            const fieldControl = answerControl.get(fieldName);
            return fieldControl.invalid && (fieldControl.dirty || fieldControl.touched);
        } else {
            const control = this.investigationForm.get(fieldName);
            return control.invalid && (control.dirty || control.touched);
        }
    }

    getFieldError(fieldName: string, answerIndex?: number): string {
        if (answerIndex !== undefined) {
            const answerControl = this.answersArray.at(answerIndex);
            const fieldControl = answerControl.get(fieldName);
            return this.getErrorMessage(fieldControl.errors);
        } else {
            const control = this.investigationForm.get(fieldName);
            return this.getErrorMessage(control.errors);
        }
    }

    private getErrorMessage(errors: any): string {
        if (!errors) return '';
        
        if (errors.required) return 'This field is required';
        if (errors.maxlength) return `Maximum length is ${errors.maxlength.requiredLength}`;
        if (errors.minlength) return `Minimum length is ${errors.minlength.requiredLength}`;
        if (errors.email) return 'Please enter a valid email address';
        if (errors.pattern) return 'Please enter a valid format';
        
        return 'Invalid input';
    }

    /**
     * Extract dropdown value from combined answer string
     * Expected format: "Dropdown Value|Text Value" or just "Dropdown Value"
     */
    private extractDropdownFromAnswer(answer: string): string {
        if (!answer) return '';
        const parts = answer.split('|');
        return parts[0] || '';
    }

    /**
     * Extract text value from combined answer string
     * Expected format: "Dropdown Value|Text Value" or just "Dropdown Value"
     */
    private extractTextFromAnswer(answer: string): string {
        if (!answer) return '';
        const parts = answer.split('|');
        return parts.length > 1 ? parts[1] : '';
    }

    /**
     * Combine dropdown and text values into single answer string
     * Format: "Dropdown Value|Text Value"
     */
    private combineDropdownAndText(dropdownValue: string, textValue: string): string {
        if (!dropdownValue && !textValue) return '';
        if (!dropdownValue) return textValue;
        if (!textValue) return dropdownValue;
        return `${dropdownValue}|${textValue}`;
    }
}



// WEBPACK FOOTER //
// /Users/brianyoung/Documents/Development/hfg/datalive-core/gae-web/datalive/client/src/app/Vehicle-file/components/Investigation/investigation.component.ts